import React, { useEffect, useState, useMemo } from "react";
import CustomTable from "../../components/common/CustomTable";
import { MdDone, MdClose, MdRemoveRedEye } from "react-icons/md";
import CustomSidebar from "../../components/common/CustomSidebar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { userId } from "../../utils/storage";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/Constant/Notification";
import { ToastContainer } from "react-toastify";

import html2pdf from "html2pdf.js";
import teamService from "../../components/Services/TeamService";
import {  formatDate } from "../../components/Constant/Constant";
import { IoMdImages } from "react-icons/io";
import AllowanceFilter from "./AllowanceFilter";
import LeadTable from "../../components/Form/LeadTable/LeadTable";
import allowanceService from "../Services/allowanceService";
import CustomChip from "../../components/common/CustomChip";
import { useNavigate } from "react-router-dom";
import { formatToIndianDate } from "../../utils/dateFormatter";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomButton from "../../components/common/CustomButton";

const AllowanceList = () => {
  const [activeTab, setActiveTab] = useState("PENDING");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [requestData, setRequestData] = useState([]);
  const [nextCursor, setNextCursor] = useState("");
  const [previousCursor, setPreviousCursor] = useState("");
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPermissionType, setSelectedPermissionType] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [cache, setCache] = useState(new Map());
  const [cursorHistory, setCursorHistory] = useState([""]);
  const navigate = useNavigate();

  const cacheKey = useMemo(() => {
    return `${activeTab}-${limit}-${currentPage}`;
  }, [activeTab, limit, currentPage]);

  const fetchData = async (
    direction = "next",
    cursor = "",
    page = 1,
    filterParams = {}
  ) => {
    const pageKey = `${cacheKey}`;

    if (direction !== "filter" && cache.get(pageKey)) {
      const cachedData = cache.get(pageKey);
      setRequestData(cachedData.data);
      setNextCursor(cachedData.nextCursor);
      setPreviousCursor(cachedData.previousCursor);
      setHasMore(cachedData.hasMore);
      setLoading(false);
      return;
    }


    setLoading(true);
    try {
      const body = {
        next: direction === "next" ? cursor : "",
        limit: Number(limit),
        direction,
        userId:  filterParams.selectedUser ? filterParams.selectedUser.value : null,
       
        searchKey: searchTerm,
        teamId: filterParams.selectedTeam ? filterParams.selectedTeam.value : null,
        startDate: filterParams.startDate ? formatDate(filterParams.startDate) : null,
        endDate: filterParams.endDate ? formatDate(filterParams.endDate) : null,
      };

      const response = await allowanceService.getAllowanceAggregatesList(body);

      if (response && response.data) {
        const mappedData = response.data.users.map((user) => ({
          id: user.userId,
          userId: user.userId,
          userName: user.userName,
          empId: user.empId || "N/A",
          teamName: user.teamName || "No Team",
          pendingRequestCount: user.pendingRequestCount || 0,
          travelAllowanceTotal: user.travelAllowanceTotal || 0,
          otherAllowanceTotal: user.otherAllowanceTotal || 0,
          totalAllowanceAmount: user.totalAllowanceAmount || 0,
        }));

        const newNextCursor = response.data.pagination?.next || "";
        const newPreviousCursor = response.data.pagination?.prev || "";
        const newHasMore = mappedData.length === limit;

        if (direction !== "filter") {
          setCache((prevCache) => {
            const newCache = new Map(prevCache);
            newCache.set(pageKey, {
              data: mappedData,
              nextCursor: newNextCursor,
              previousCursor: newPreviousCursor,
              hasMore: newHasMore,
            });
            return newCache;
          });
        }

        setRequestData(mappedData);
        setNextCursor(newNextCursor);
        setPreviousCursor(newPreviousCursor);
        setHasMore(newHasMore);
      }
    } catch (error) {
      console.error("Error fetching allowance request list:", error);
      showErrorToast("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    setCursorHistory([""]);
    fetchData("next", "", 1);
  }, [activeTab, limit]);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await teamService.getTeamName();
        const data = response.data?.teams || [];
        setTeams(data.map((team) => ({ label: team.name, value: team.teamId })));
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    };
    fetchTeams();
  }, []);

  const handleApplyFilter = (filterParams) => {
    if (filterParams.startDate && !filterParams.endDate) {
      showErrorToast("End Date is required when Start Date is selected.");
      return;
    }

    setStartDate(filterParams.startDate);
    setEndDate(filterParams.endDate);
    setSelectedTeam(filterParams.selectedTeam);
    setSelectedUser(filterParams.selectedUser);
    setCurrentPage(1);
    setCursorHistory([""]);
    setCache(new Map());
    fetchData("filter", "", 1, filterParams);
    setIsFilterOpen(false);
  };

  const handleClearFilters = () => {
    const filterParams = {
      startDate: null,
      endDate: null,
      selectedTeam: null,
      selectedUser: null,
    };

    setSelectedPermissionType(null);
    setSelectedTeam(null);
    setSelectedUser(null);
    setStartDate(null);
    setEndDate(null);
    setSearchTerm("");
    setCurrentPage(1);
    setCursorHistory([""]);
    setCache(new Map());
    fetchData("filter", "", 1, filterParams);
    setIsFilterOpen(false);
  };

  const handlePageChange = (token, direction) => {
    if (direction === "size") {
      setLimit(token);
      setCurrentPage(1);
      setCursorHistory([""]);
      fetchData("next", "", 1);
    } else if (direction === "next") {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setCursorHistory((prev) => {
        const newHistory = [...prev];
        if (newHistory.length <= nextPage - 1) {
          newHistory.push(nextCursor);
        }
        return newHistory;
      });
      fetchData("next", nextCursor, nextPage);
    } else if (direction === "previous") {
      const prevPage = Math.max(currentPage - 1, 1);
      setCurrentPage(prevPage);
      const prevCursor = cursorHistory[prevPage - 1] || "";
      fetchData("previous", prevCursor, prevPage);
    }
  };

  const handleInfoClick = (employee) => {
    navigate(`/hr-employee-analytics/user-info`, {
      state: { employeeId: employee?.userId },
    });
  };

  const handleRemove = (filterKey) => {
    const filterParams = {
      startDate,
      endDate,
      selectedTeam,
      selectedUser,
    };

    switch (filterKey) {
      case "team":
        filterParams.selectedTeam = null;
        filterParams.selectedUser = null;
        setSelectedTeam(null);
        setSelectedUser(null);
        break;
      case "user":
        filterParams.selectedUser = null;
        setSelectedUser(null);
        break;
      case "startDate":
        filterParams.startDate = null;
        filterParams.endDate = null;
        setStartDate(null);
        setEndDate(null);
        break;
      default:
        return;
    }

    setCurrentPage(1);
    setCursorHistory([""]);
    setCache(new Map());
    fetchData("filter", "", 1, filterParams);
  };

  const handleReport = async () => {
    if (!startDate || !endDate) {
      showErrorToast("Please select both Start Date and End Date to download the report.");
      return;
    }

    setLoadingDownload(true);
    try {
      const body = {
        userId: selectedUser ? selectedUser.value : null,
        teamId: selectedTeam ? selectedTeam.value : null,
        startDate: startDate ? formatDate(startDate) : null,
        endDate: endDate ? formatDate(endDate) : null,
      };

      const response = await allowanceService.getAllowanceAggregateExcel(body);

            const reportData = response.data;
            if (!Array.isArray(reportData) || reportData.length === 0) {
              showErrorToast("No data found for the selected criteria.");
              return;
            }
      
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Report");
      
            const headers = Object.keys(reportData[0]);
            worksheet.columns = headers.map((header) => ({
              header: header.trim(),
              key: header,
              width: 20,
            }));
      
            // Add date information in the first row as a merged cell
            const todaysDate = new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: '2-digit', 
              year: 'numeric'
            });

            let dateInfoText;
            if(startDate && endDate) {
              dateInfoText = `Report Generated on: ${todaysDate} | Date Range: ${startDate ? formatDate(startDate) : ''} to ${endDate ? formatDate(endDate) : ''}`;
            }
            else {
              dateInfoText = `Report Generated on: ${todaysDate}`;
            }
            
           // const dateInfoText = `Report Generated on: ${todaysDate} | Date Range: ${startDate ? formatToIndianDate(startDate) : ''} to ${endDate ? formatToIndianDate(endDate) : ''}`;
            const leadReportType = selectedUser ? selectedUser?.label : selectedTeam ? "team" : "overall";
            
            // Insert a new row at the top
            worksheet.insertRow(1);
            
            // Merge cells across all columns in the first row
            worksheet.mergeCells(1, 1, 1, headers.length);
            
            // Set the merged cell content and styling
            const dateCell = worksheet.getCell(1, 1);
            dateCell.value = dateInfoText;
            dateCell.font = { bold: true, size: 12, color: { argb: "FF000000" } };
            dateCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFE6E6E6" },
            };
            dateCell.alignment = { vertical: "middle", horizontal: "center" };
            dateCell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
            
            // Set row height for the date row
            worksheet.getRow(1).height = 40;
      
            // Style the header row (now row 2)
            worksheet.getRow(2).eachCell((cell) => {
              cell.font = { bold: true, color: { argb: leadReportType === "booking" ? "FF0070C0" : "FF000000" } };
              cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: leadReportType === "booking" ? "F8CBAD" : "C4C6E4" },
              };

              cell.alignment = { vertical: "middle", horizontal: "center" };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            });
      
            reportData.forEach((item) => {
              worksheet.addRow(item);
            });
      
            worksheet.eachRow((row, rowNumber) => {
              if (rowNumber > 2) { // Skip the date row (1) and header row (2)
                row.height = 30;
                row.eachCell((cell) => {
                  cell.alignment = { vertical: "middle", horizontal: "center" };
                  cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                  };
                });
              } else if (rowNumber === 2) { // Header row
                row.height = 60;
              }
            });
      
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, `${leadReportType}_report_${formatDate(startDate)}_to_${formatDate(endDate)}.xlsx`);

          } catch (error) {
            console.error("Error downloading report:", error);
            showErrorToast("Failed to download report.");
          } finally {
            setLoadingDownload(false);
          }
  }

  const columns = [
    {
      key: "userName",
      title: "User Name",
      render: (value, row) => (
        <div
        title={value}
          onClick={() => handleInfoClick(row)}
          className="line-clamp-2 capitalize text-wrap truncate cursor-pointer text-center"
        >
          <div className="line-clamp-2 truncate ">{value}</div>
          <div className="text-sm text-customGreen">Emp ID: {row.empId}</div>
        </div>
      ),
    },
    {
      key: "teamName",
      title: "Team",
      render: (value) => (
        <div className="text-center">
          <div className="line-clamp-1  capitalize text-wrap truncate">{value}</div>
        </div>
      ),
    },
    {
      key: "pendingRequestCount",
      title: "Pending Request",
      render: (value) => <div className="text-center">{value}</div>,
    },
    {
      key: "travelAllowanceTotal",
      title: "Approved Travel Allowance",
      render: (value) => <div className="text-center">₹{value}</div>,
    },
    {
      key: "otherAllowanceTotal",
      title: "Approved Other Allowance",
      render: (value) => <div className="text-center">₹{value}</div>,
    },
    {
      key: "totalAllowanceAmount",
      title: "Total Allowance Amount",
      render: (value) => <div className="text-center">₹{value}</div>,
    },
  ];

  return (
    <div>
      <ToastContainer />
      <div className="">
        <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedTeam && (
            <CustomChip value={selectedTeam.label} onRemove={() => handleRemove("team")} />
          )}
          {selectedUser && (
            <CustomChip value={selectedUser.label} onRemove={() => handleRemove("user")} />
          )}
          {startDate && endDate && (
            <CustomChip
              value={`Start: ${formatToIndianDate(startDate)} - End: ${formatToIndianDate(endDate)}`}
              onRemove={() => handleRemove("startDate")}
            />
          )}
        </div>
      <CustomButton
          label="Download Report"
          isDisabled={loadingDownload}
          onClick={handleReport}
          className="mb-4"
        />
        </div>
        <LeadTable
          isSearchable={true}
          disPlay={false}
          isPagination={true}
          data={requestData}
          columns={columns}
          loading={loading}
          setSearchQuery={setSearchTerm}
          searchQuery={searchTerm}
          onPageChange={handlePageChange}
          handleSearchLead={() => {
            setCurrentPage(1);
            setCursorHistory([""]);
            setCache(new Map());
            fetchData("filter", "", 1);
          }}
          currentPage={currentPage}
          handleFilterLeads={() => {
            setIsFilterOpen(true);
          }}
          pageSize={limit}
          hasMoreLeads={hasMore}
        />
        <AllowanceFilter
          visible={isFilterOpen}
          onHide={() => setIsFilterOpen(false)}
          teams={teams}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          selectedPermissionType={selectedPermissionType}
          setSelectedPermissionType={setSelectedPermissionType}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          handleApplyFilter={handleApplyFilter}
          clearFilters={handleClearFilters}
        />
      </div>
    </div>
  );
};

export default AllowanceList;